import moment from "moment";
import React, { FC, useState } from "react";
import { useParams } from "react-router-dom";

import {
  useCancelClientRequestMutation,
  useDeleteClientRequestMutation,
  useGetWorkClientRequestsQuery,
  useRemindClientRequestMutation,
  useSendClientRequestMutation,
} from "../../slices/ClientRequestsSlice";
import { modalCancel, modalPrimary } from "../clientPortal/PortalModal";
import { FileItem } from "../files/files.types";
import { useFileOpener } from "../files/useFileOpener";
import { ClientRequest, RequestFile } from "./clientRequests.types";
import {
  answerText,
  kindLabel,
  progressOf,
  reminderText,
  STATUS_PILL,
  statusLine,
} from "./clientRequests.utils";
import { RequestListModal, serverText } from "./RequestListModal";

/** A request file as the Documents file opener expects it (a client file). */
const asFileItem = (f: RequestFile): FileItem => ({
  kind: "client",
  id: f.documentId,
  name: f.name,
  size: f.size ?? null,
  addedAt: f.uploadedAt ?? null,
  folderId: null,
  folderPath: null,
  workId: null,
  workName: null,
  addedBy: null,
  addedByKind: "client",
  clientCanSee: true,
  sharedDirectly: false,
  fromClient: true,
  isNew: false,
  note: null,
  customerId: null,
  customerName: null,
  isOrganization: false,
  contactId: null,
  businessContactId: null,
  deletedAt: null,
  deletedBy: null,
  daysLeft: null,
});

const linkBtn =
  "text-[12.5px] font-bold text-[#6B4EFF] hover:text-[#4B2FC7] disabled:opacity-50";

/** Work item tab "Client requests": the work's request lists, progress, answers and files. */
export const WorkClientRequests: FC = () => {
  const { workId = "" } = useParams();
  const { data, isLoading, error } = useGetWorkClientRequestsQuery(
    { workId },
    { skip: !workId },
  );
  const [windowFor, setWindowFor] = useState<ClientRequest | "new" | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [problem, setProblem] = useState<string>("");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);
  const [send] = useSendClientRequestMutation();
  const [remind, remindState] = useRemindClientRequestMutation();
  const [cancel] = useCancelClientRequestMutation();
  const [remove] = useDeleteClientRequestMutation();
  const { openFile, preview } = useFileOpener(setProblem);

  const say = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 5000);
  };
  const run = async (
    action: Promise<{ data?: unknown } | { error: unknown }>,
    ok: string,
    fallback: string,
  ) => {
    setProblem("");
    const result = await action;
    if ("error" in result) setProblem(serverText(result.error, fallback));
    else say(ok);
  };

  if (isLoading)
    return (
      <div className={"p-6 text-sm font-semibold text-[#6B7185]"}>Loading…</div>
    );
  if (error)
    return (
      <div className={"p-6 text-sm font-semibold text-[#B42318]"}>
        {serverText(error, "Client requests couldn't be loaded.")}
      </div>
    );
  if (data && !data.available)
    return (
      <div
        className={
          "rounded-[14px] border border-[#EDEFF3] bg-white p-6 text-sm font-semibold text-[#6B7185]"
        }>
        {data.message || "Client requests need a database update first."}
      </div>
    );

  const requests = data?.requests ?? [];

  return (
    <div
      className={"flex flex-col gap-4 font-[Inter,sans-serif] text-[#1F2433]"}>
      <div className={"flex flex-wrap items-center gap-3"}>
        <div className={"flex flex-1 flex-col"}>
          <h2 className={"m-0 text-[17px] font-extrabold"}>Client requests</h2>
          <span className={"text-[13px] font-medium text-[#6B7185]"}>
            Ask your client for files and answers. They complete the list in
            your client portal, with automatic reminders until it's done.
          </span>
        </div>
        <button
          type={"button"}
          className={modalPrimary}
          onClick={() => setWindowFor("new")}>
          + New request list
        </button>
      </div>

      {toast && (
        <div
          role={"status"}
          className={
            "rounded-[10px] bg-[#E6F6EE] px-3 py-2 text-[13px] font-semibold text-[#12805C]"
          }>
          {toast}
        </div>
      )}
      {problem && (
        <div
          role={"alert"}
          className={
            "rounded-[10px] bg-[#FDECEC] px-3 py-2 text-[13px] font-semibold text-[#B42318]"
          }>
          {problem}
        </div>
      )}

      {requests.length === 0 && (
        <div
          className={
            "flex flex-col items-center gap-2 rounded-[14px] border border-dashed border-[#DCE2EA] bg-white px-6 py-10 text-center"
          }>
          <span className={"text-[15px] font-extrabold"}>
            No request lists yet
          </span>
          <span className={"max-w-[460px] text-[13px] text-[#6B7185]"}>
            Make a list of the documents and answers you need, for example "T4
            slips" or "Did you move this year?". Your client gets an email and
            completes it in the client portal.
          </span>
        </div>
      )}

      {requests.map((r) => {
        const pill = STATUS_PILL[r.status];
        const p = progressOf(r);
        const expanded = open[r.id] ?? r.status === "sent";
        return (
          <section
            key={r.id}
            className={
              "flex flex-col gap-3 rounded-[14px] border border-[#EDEFF3] bg-white p-4"
            }>
            <div className={"flex flex-wrap items-start gap-3"}>
              <div className={"flex min-w-0 flex-1 flex-col gap-1"}>
                <div className={"flex flex-wrap items-center gap-2"}>
                  <span className={"text-[15px] font-extrabold"}>
                    {r.title}
                  </span>
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-[11.5px] font-bold"
                    }
                    style={{ color: pill.color, background: pill.bg }}>
                    {pill.label}
                  </span>
                </div>
                <span className={"text-[12.5px] font-medium text-[#6B7185]"}>
                  {statusLine(r)}
                </span>
              </div>
              <div className={"flex flex-wrap items-center gap-2"}>
                {r.status === "draft" && (
                  <>
                    <button
                      type={"button"}
                      className={modalPrimary}
                      onClick={() =>
                        run(
                          send(r.id),
                          "Sent to your client.",
                          "The request list couldn't be sent.",
                        )
                      }>
                      Send to client
                    </button>
                    <button
                      type={"button"}
                      className={modalCancel}
                      onClick={() => setWindowFor(r)}>
                      Edit
                    </button>
                    <button
                      type={"button"}
                      className={linkBtn}
                      onClick={() =>
                        run(
                          remove(r.id),
                          "Draft deleted.",
                          "The draft couldn't be deleted.",
                        )
                      }>
                      Delete
                    </button>
                  </>
                )}
                {r.status === "sent" && (
                  <>
                    <button
                      type={"button"}
                      className={modalCancel}
                      disabled={remindState.isLoading}
                      onClick={() =>
                        run(
                          remind(r.id),
                          "Reminder sent.",
                          "The reminder couldn't be sent.",
                        )
                      }>
                      Remind now
                    </button>
                    <button
                      type={"button"}
                      className={modalCancel}
                      onClick={() => setWindowFor(r)}>
                      Edit
                    </button>
                    {confirmCancel === r.id ? (
                      <span
                        className={
                          "flex items-center gap-2 text-[12.5px] font-semibold"
                        }>
                        Stop asking?
                        <button
                          type={"button"}
                          className={linkBtn}
                          onClick={() => {
                            setConfirmCancel(null);
                            run(
                              cancel(r.id),
                              "Request list cancelled. No more reminders.",
                              "The request list couldn't be cancelled.",
                            );
                          }}>
                          Yes, cancel
                        </button>
                        <button
                          type={"button"}
                          className={linkBtn}
                          onClick={() => setConfirmCancel(null)}>
                          No
                        </button>
                      </span>
                    ) : (
                      <button
                        type={"button"}
                        className={linkBtn}
                        onClick={() => setConfirmCancel(r.id)}>
                        Cancel
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {r.status !== "draft" && (
              <div className={"flex items-center gap-3"}>
                <div
                  className={
                    "h-2 flex-1 overflow-hidden rounded-full bg-[#EDEFF3]"
                  }
                  role={"progressbar"}
                  aria-valuenow={p.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}>
                  <div
                    className={"h-full rounded-full bg-[#6B4EFF]"}
                    style={{ width: `${p.percent}%` }}
                  />
                </div>
                <span className={"text-[12.5px] font-bold text-[#3A4256]"}>
                  {p.label} done
                </span>
              </div>
            )}

            <button
              type={"button"}
              className={`${linkBtn} self-start`}
              onClick={() => setOpen((o) => ({ ...o, [r.id]: !expanded }))}>
              {expanded ? "Hide items" : `Show items (${r.totalCount})`}
            </button>

            {expanded && (
              <ul className={"m-0 flex list-none flex-col gap-1.5 p-0"}>
                {r.items.map((item) => {
                  const answer = answerText(item);
                  return (
                    <li
                      key={item.id}
                      className={
                        "flex items-start gap-3 rounded-[10px] border border-[#EDEFF3] px-3 py-2"
                      }>
                      <span
                        aria-label={item.done ? "Done" : "Missing"}
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold ${
                          item.done
                            ? "bg-[#12805C] text-white"
                            : "border-[1.5px] border-[#DCE2EA] text-transparent"
                        }`}>
                        ✓
                      </span>
                      <div className={"flex min-w-0 flex-1 flex-col gap-1"}>
                        <span className={"text-[13.5px] font-semibold"}>
                          {item.label}
                          <span
                            className={
                              "ml-2 text-[11.5px] font-bold text-[#9097A8]"
                            }>
                            {kindLabel(item.kind)}
                            {item.kind === "yesno" && item.fileIfYes
                              ? " · file if yes"
                              : ""}
                          </span>
                        </span>
                        {answer && (
                          <span
                            className={
                              "whitespace-pre-wrap text-[13px] text-[#3A4256]"
                            }>
                            {answer}
                          </span>
                        )}
                        {item.files.length > 0 && (
                          <div className={"flex flex-wrap gap-1.5"}>
                            {item.files.map((f) => (
                              <button
                                key={f.documentId}
                                type={"button"}
                                onClick={() => openFile(asFileItem(f))}
                                className={
                                  "max-w-[260px] truncate rounded-[8px] border border-[#DCE2EA] bg-[#FBFBFD] px-2 py-1 text-[12px] font-semibold text-[#3A4256] hover:border-[#6B4EFF]"
                                }>
                                {f.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {item.done && item.completedAt && (
                          <span
                            className={
                              "text-[11.5px] font-medium text-[#9097A8]"
                            }>
                            {item.answeredByName
                              ? `${item.answeredByName} · `
                              : ""}
                            {moment(item.completedAt).format("MMM D, h:mm A")}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {expanded && r.status === "sent" && (
              <span className={"text-[12px] font-medium text-[#9097A8]"}>
                Reminders:{" "}
                {reminderText(r.reminderMode, r.reminderEveryDays, !!r.dueDate)}
                {r.moveToReadyWhenDone
                  ? " · moves to Ready when everything is received"
                  : ""}
                {r.repeatWithWork ? " · repeats with the work item" : ""}
              </span>
            )}
          </section>
        );
      })}

      {windowFor && (
        <RequestListModal
          workId={workId}
          editing={windowFor === "new" ? null : windowFor}
          onClose={() => setWindowFor(null)}
          onDone={(text) => {
            setWindowFor(null);
            say(text);
          }}
        />
      )}
      {preview}
    </div>
  );
};
